"use client";
import React, { useState, useEffect, lazy, Suspense } from "react";
import * as SubframeUtils from "../utils";
import { SidebarCollapsible } from "../components/SidebarCollapsible";
import { FeatherLayoutDashboard } from "@subframe/core";
import { FeatherBook } from "@subframe/core";
import { FeatherLayers } from "@subframe/core";
import { FeatherCheckSquare } from "@subframe/core";
import { FeatherHeadphones } from "@subframe/core";
import { FeatherTrendingUp } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { Avatar } from "../components/Avatar";
import { FeatherChevronsUpDown } from "@subframe/core";
import { DropdownMenu } from "../components/DropdownMenu";
import { FeatherSettings } from "@subframe/core";
import { FeatherLogOut } from "@subframe/core";
import { FeatherMenu } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherLogIn } from "@subframe/core";
import { IconButton } from "../components/IconButton";
import { Loader } from "../components/Loader";
import { useAuthContext } from "../../contexts/AuthContext";
import * as SubframeCore from "@subframe/core";

// Lazy load the settings modal to improve initial load time
const SettingsModal = lazy(() =>
  import("../../components/SettingsModal").then((module) => ({
    default: module.SettingsModal,
  }))
);

type PageType =
  | "home"
  | "dashboard"
  | "study-modules"
  | "flashcards"
  | "quizzes"
  | "audio-lessons"
  | "knowledge-tracker"
  | "settings";

interface DefaultPageLayoutRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  currentPage?: PageType;
  onNavigate?: (page: PageType) => void;
  onNewQuery?: () => void;
  onShowAuth?: () => void;
  className?: string;
  isModalOpen?: boolean; // <-- Add prop
}

const DefaultPageLayoutRoot = React.forwardRef<
  HTMLElement,
  DefaultPageLayoutRootProps
>(function DefaultPageLayoutRoot(
  {
    children,
    currentPage = "home",
    onNavigate,
    onNewQuery,
    onShowAuth,
    className,
    isModalOpen, // <-- Receive prop
    ...otherProps
  }: DefaultPageLayoutRootProps,
  ref
) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { user, signOut, loading } = useAuthContext();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // <-- MODIFICATION: Add this effect to automatically close the sidebar -->
  useEffect(() => {
    if (isModalOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isModalOpen]);

  useEffect(() =>{
  if (loading) {
    return
  }
  if (!user && onShowAuth && currentPage !== "home" && currentPage !== "settings") {
      onShowAuth();
    }
  }, [loading, user, currentPage, onShowAuth]);

  const handleLogoClick = () => {
    if (onNavigate) onNavigate("home");
  };

  const handleAppNameClick = () => {
    if (onNavigate) onNavigate("home");
  };

  const createNavHandler = (page: PageType) => () => {
    if (!user && onShowAuth && page !== "home" && page !== "settings") {
      onShowAuth();
      return;
    }
    if (onNavigate) onNavigate(page);
    setIsMobileMenuOpen(false);
  };

  const handleDashboardClick = createNavHandler("dashboard");
  const handleStudyModulesClick = createNavHandler("study-modules");
  const handleFlashcardsClick = createNavHandler("flashcards");
  const handleQuizzesClick = createNavHandler("quizzes");
  const handleAudioLessonsClick = createNavHandler("audio-lessons");
  const handleKnowledgeTrackerClick = createNavHandler("knowledge-tracker");

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleSignOutClick = async () => {
    await signOut();
    if (onNavigate) onNavigate("home");
  };

  const handleSignInClick = () => {
    if (onShowAuth) onShowAuth();
    setIsMobileMenuOpen(false);
  };

  const handleNewQueryClick = () => {
    if (onNewQuery) {
      onNewQuery();
    } else if (onNavigate) {
      onNavigate("home");
    }
    setIsMobileMenuOpen(false);
  };

  const getUserDisplayName = () => {
    if (!user) return "Guest";
    return (
      user.user_metadata?.display_name || user.email?.split("@")[0] || "User"
    );
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex h-screen w-full items-start relative",
        className
      )}
      ref={ref as any}
      {...otherProps}
    >
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between bg-default-background border-b border-solid border-neutral-border px-4 py-3">
        <div className="flex items-center gap-3">
          <IconButton
            variant="neutral-tertiary"
            icon={isMobileMenuOpen ? <FeatherX /> : <FeatherMenu />}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
          <div className="flex items-center gap-3">
            <img
              className="h-8 w-8 flex-none object-cover cursor-pointer hover:opacity-80 transition-opacity"
              src="/brain.png"
              onClick={handleLogoClick}
              alt="Logo"
              loading="eager"
            />
            <span
              className="text-heading-3 font-heading-3 bg-gradient-to-r from-brand-600 to-brand-700 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleAppNameClick}
            >
              MasteryMint
            </span>
          </div>
        </div>
        
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={SubframeUtils.twClassNames(
          "md:hidden fixed top-16 left-0 bottom-0 z-50 w-64 bg-default-background border-r border-solid border-neutral-border transform transition-transform duration-300 ease-in-out",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-1 px-3 py-4 overflow-auto">
            <div
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-brand-50 active:bg-brand-100 mb-2 border border-solid border-brand-200 bg-brand-50"
              onClick={handleNewQueryClick}
            >
              <SubframeCore.IconWrapper className="text-heading-3 font-heading-3 text-brand-600">
                <FeatherPlus />
              </SubframeCore.IconWrapper>
              <span className="line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-brand-600">
                New Query
              </span>
            </div>
            <div className="w-full h-px bg-neutral-border mb-2" />

            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "dashboard",
                }
              )}
              onClick={handleDashboardClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "dashboard" }
                )}
              >
                <FeatherLayoutDashboard />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "dashboard" }
                )}
              >
                Dashboard
              </span>
            </div>
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "study-modules",
                }
              )}
              onClick={handleStudyModulesClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "study-modules" }
                )}
              >
                <FeatherBook />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "study-modules" }
                )}
              >
                Study Modules
              </span>
            </div>
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "flashcards",
                }
              )}
              onClick={handleFlashcardsClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "flashcards" }
                )}
              >
                <FeatherLayers />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "flashcards" }
                )}
              >
                Flashcards
              </span>
            </div>
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "quizzes",
                }
              )}
              onClick={handleQuizzesClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "quizzes" }
                )}
              >
                <FeatherCheckSquare />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "quizzes" }
                )}
              >
                Quizzes
              </span>
            </div>
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "audio-lessons",
                }
              )}
              onClick={handleAudioLessonsClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "audio-lessons" }
                )}
              >
                <FeatherHeadphones />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "audio-lessons" }
                )}
              >
                Audio Lessons
              </span>
            </div>
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100",
                {
                  "bg-brand-50 hover:bg-brand-50 active:bg-brand-100":
                    currentPage === "knowledge-tracker",
                }
              )}
              onClick={handleKnowledgeTrackerClick}
            >
              <SubframeCore.IconWrapper
                className={SubframeUtils.twClassNames(
                  "text-heading-3 font-heading-3 text-neutral-600",
                  { "text-brand-700": currentPage === "knowledge-tracker" }
                )}
              >
                <FeatherTrendingUp />
              </SubframeCore.IconWrapper>
              <span
                className={SubframeUtils.twClassNames(
                  "line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-neutral-600",
                  { "text-brand-700": currentPage === "knowledge-tracker" }
                )}
              >
                Knowledge Tracker
              </span>
            </div>
          </div>
          <div className="flex w-full items-center gap-4 border-t border-solid border-neutral-border px-6 py-6">
            {isMounted && user ? (
              <SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Trigger asChild={true}>
                  <div className="flex grow shrink-0 basis-0 items-center gap-4 cursor-pointer">
                    <div className="flex grow shrink-0 basis-0 items-start gap-4">
                      <Avatar image={user.user_metadata?.avatar_url}>
                        {getUserInitials()}
                      </Avatar>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {getUserDisplayName()}
                      </span>
                    </div>
                    <FeatherChevronsUpDown className="text-body font-body text-default-font" />
                  </div>
                </SubframeCore.DropdownMenu.Trigger>
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherSettings />}
                    onClick={handleSettingsClick}
                  >
                    Settings
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownDivider />
                  <DropdownMenu.DropdownItem
                    icon={<FeatherLogOut />}
                    onClick={handleSignOutClick}
                  >
                    Sign out
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Root>
            ) : (
              <div className="flex w-full items-center justify-center">
                <IconButton
                  variant="brand-primary"
                  icon={<FeatherLogIn />}
                  onClick={handleSignInClick}
                />
                <span className="ml-2 text-caption font-caption text-subtext-color">
                  Sign in to access features
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <SidebarCollapsible
          header={
            <div className="flex items-center gap-3">
              <img
                className="h-8 w-8 flex-none object-cover cursor-pointer hover:opacity-80 transition-opacity"
                src="/brain.png"
                onClick={handleLogoClick}
                alt="Logo"
                loading="eager"
              />
              <span
                className="text-heading-3 font-heading-3 bg-gradient-to-r from-brand-600 to-brand-700 bg-clip-text text-transparent group-hover/e732d4fd:inline-block hidden cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleAppNameClick}
              >
                MasteryMint
              </span>
            </div>
          }
          footer={
            isMounted && user ? (
              <SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Trigger asChild={true}>
                  <div className="flex grow shrink-0 basis-0 items-center gap-4 cursor-pointer">
                    <div className="flex grow shrink-0 basis-0 items-start gap-4">
                      <Avatar image={user.user_metadata?.avatar_url}>
                        {getUserInitials()}
                      </Avatar>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {getUserDisplayName()}
                      </span>
                    </div>
                    <FeatherChevronsUpDown className="text-body font-body text-default-font" />
                  </div>
                </SubframeCore.DropdownMenu.Trigger>
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={<FeatherSettings />}
                    onClick={handleSettingsClick}
                  >
                    Settings
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownDivider />
                  <DropdownMenu.DropdownItem
                    icon={<FeatherLogOut />}
                    onClick={handleSignOutClick}
                  >
                    Sign out
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Root>
            ) : (
              <div className="flex w-full items-center justify-center">
                <IconButton
                  variant="brand-primary"
                  icon={<FeatherLogIn />}
                  onClick={handleSignInClick}
                />
              </div>
            )
          }
        >
          <SidebarCollapsible.NavItem
            icon={<FeatherPlus />}
            onClick={handleNewQueryClick}
            className="mb-2 border border-solid border-brand-200 bg-brand-50 hover:bg-brand-100"
          >
            <span className="text-brand-600 font-bold">New Query</span>
          </SidebarCollapsible.NavItem>
          <div className="w-full h-px bg-neutral-border mb-2" />
          <SidebarCollapsible.NavItem
            icon={<FeatherLayoutDashboard />}
            selected={currentPage === "dashboard"}
            onClick={handleDashboardClick}
          >
            Dashboard
          </SidebarCollapsible.NavItem>
          <SidebarCollapsible.NavItem
            icon={<FeatherBook />}
            selected={currentPage === "study-modules"}
            onClick={handleStudyModulesClick}
          >
            Study Modules
          </SidebarCollapsible.NavItem>
          <SidebarCollapsible.NavItem
            icon={<FeatherLayers />}
            selected={currentPage === "flashcards"}
            onClick={handleFlashcardsClick}
          >
            Flashcards
          </SidebarCollapsible.NavItem>
          <SidebarCollapsible.NavItem
            icon={<FeatherCheckSquare />}
            selected={currentPage === "quizzes"}
            onClick={handleQuizzesClick}
          >
            Quizzes
          </SidebarCollapsible.NavItem>
          <SidebarCollapsible.NavItem
            icon={<FeatherHeadphones />}
            selected={currentPage === "audio-lessons"}
            onClick={handleAudioLessonsClick}
          >
            Audio Lessons
          </SidebarCollapsible.NavItem>
          <SidebarCollapsible.NavItem
            icon={<FeatherTrendingUp />}
            selected={currentPage === "knowledge-tracker"}
            onClick={handleKnowledgeTrackerClick}
          >
            Knowledge Tracker
          </SidebarCollapsible.NavItem>
        </SidebarCollapsible>
      </div>

      {/* Main Content */}
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 self-stretch overflow-y-auto bg-default-background pt-16 md:pt-0">
        {children}
      </div>

      {/* Settings Modal - Lazy loaded */}
      {isSettingsOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75">
              <Loader size="large" />
            </div>
          }
        >
          <SettingsModal
            open={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
          />
        </Suspense>
      )}
    </div>
  );
});

export const DefaultPageLayout = DefaultPageLayoutRoot;
